import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HiOutlinePencilAlt, HiOutlineCheck } from 'react-icons/hi';
import { setNote as setBasketNote } from '../../../../store/basket/basketSlice';

const NOTE_MAX = 240;

/**
 * Premium "Add note" modal.
 * - Pencil icon hero + direct heading.
 * - Larger textarea with accent focus ring + live character count.
 * - Clean Save/Cancel footer.
 */
const NoteModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const basket = useSelector((state) => state.basket.basket);
  const [value, setValue] = useState(basket.note || '');

  const handleSave = () => {
    dispatch(setBasketNote(value.trim()));
    onClose?.();
  };

  const handleClear = () => {
    setValue('');
    dispatch(setBasketNote(''));
  };

  const hasNote = !!basket.note;

  return (
    <div
      className="w-full max-w-[480px] mx-auto relative"
      style={{
        padding: '28px 26px',
        fontFamily: 'Geist, system-ui, sans-serif',
      }}
    >
      <style>{`
        @keyframes nmFadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .nm-fade { animation: nmFadeUp 0.42s cubic-bezier(0.2, 0.8, 0.2, 1) both; }
        .nm-textarea:focus {
          border-color: var(--ui-ink) !important;
          box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.08) !important;
        }
      `}</style>

      {/* Icon hero */}
      <div
        className="nm-fade grid place-items-center mx-auto mb-4"
        style={{
          width: '52px',
          height: '52px',
          borderRadius: '14px',
          background: 'var(--accent-soft, var(--site-color-10))',
          color: 'var(--accent, var(--site-color))',
          border: '1px solid var(--ui-line)',
        }}
      >
        <HiOutlinePencilAlt size={22} />
      </div>

      <h2
        className="nm-fade text-center"
        style={{
          animationDelay: '0.04s',
          fontSize: '22px',
          fontWeight: 800,
          letterSpacing: '-0.025em',
          color: 'var(--ui-ink)',
          lineHeight: 1.15,
          marginBottom: '6px',
        }}
      >
        {hasNote ? 'Edit your note' : 'Leave a note for the kitchen'}
      </h2>
      <p
        className="nm-fade text-center mb-5"
        style={{
          animationDelay: '0.08s',
          fontSize: '13.5px',
          color: 'var(--ui-ink-2)',
          lineHeight: 1.5,
        }}
      >
        Allergies, preferences, or anything the chef should know.
      </p>

      {/* Textarea */}
      <div className="nm-fade" style={{ animationDelay: '0.1s' }}>
        <textarea
          autoFocus
          rows={4}
          value={value}
          onChange={(e) => setValue(e.target.value.slice(0, NOTE_MAX))}
          placeholder="e.g. No onions, extra napkins, light on the sauce…"
          className="nm-textarea w-full outline-none resize-none"
          style={{
            padding: '14px 16px',
            borderRadius: '14px',
            background: 'var(--ui-surface)',
            border: '1px solid var(--ui-line-2)',
            fontFamily: 'inherit',
            fontSize: '14px',
            color: 'var(--ui-ink)',
            lineHeight: 1.5,
            transition: 'border-color 0.15s, box-shadow 0.15s',
          }}
        />
        <div
          className="flex items-center justify-between mt-[6px]"
          style={{ fontSize: '11.5px', fontWeight: 500 }}
        >
          <span style={{ color: 'var(--ui-ink-3)' }}>
            Up to {NOTE_MAX} characters
          </span>
          <span
            style={{
              fontFamily: 'Geist Mono, monospace',
              color: value.length > NOTE_MAX - 20 ? '#B91C1C' : 'var(--ui-ink-3)',
            }}
          >
            {value.length}/{NOTE_MAX}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div
        className="nm-fade flex items-center gap-2 mt-6"
        style={{ animationDelay: '0.14s' }}
      >
        {hasNote && (
          <button
            type="button"
            onClick={handleClear}
            className="transition-colors"
            style={{
              padding: '11px 16px',
              borderRadius: '12px',
              background: 'transparent',
              color: 'var(--ui-ink-2)',
              border: '1px solid var(--ui-line-2)',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Clear
          </button>
        )}
        <button
          type="button"
          onClick={handleSave}
          className="flex-1 inline-flex items-center justify-center gap-2 font-bold transition-transform"
          style={{
            height: '44px',
            borderRadius: '12px',
            background: 'var(--accent, var(--site-color))',
            color: 'var(--accent-fg, #FFFFFF)',
            border: 'none',
            fontSize: '14px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            boxShadow:
              '0 10px 24px -8px var(--accent-soft, var(--site-color-10)), 0 2px 6px rgba(15, 23, 42, 0.08)',
          }}
        >
          <HiOutlineCheck size={15} strokeWidth={2.5} />
          {hasNote ? 'Save changes' : 'Save note'}
        </button>
      </div>
    </div>
  );
};

export default NoteModal;
